import { Check, Loader2 } from 'lucide-react'
import { debounce } from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import useSWR from 'swr'

import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { useAuthToken } from '@/hooks/useAuthToken'
import { createFetcher, createUpdater } from '@/services/api'
import { userService } from '@/services/userService'
import type { AccessibilityPreference, AccessibilitySupportArea } from '@/types/user'

const DEFAULT_PREFERENCE: AccessibilityPreference = {
  may_benefit: false,
  high_contrast_enabled: false,
  support_areas: [],
}

const SAVE_DEBOUNCE_MS = 600

const SUPPORT_AREAS: readonly AccessibilitySupportArea[] = [
  'reading_viewing_content',
  'audio_video_content',
  'site_navigation_interaction',
  'other',
]

export function AccessibilityPreferences() {
  const { t } = useTranslation()
  const { getToken, isSignedIn } = useAuthToken()
  const fetcher = useMemo(() => createFetcher(getToken), [getToken])
  const updater = useMemo(() => createUpdater(getToken), [getToken])
  const preferencesUrl = userService.getAccessibilityPreferencesUrl()
  const [preference, setPreference] = useState<AccessibilityPreference>(DEFAULT_PREFERENCE)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const dataRef = useRef<AccessibilityPreference | undefined>(undefined)
  const savedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const { data, isLoading, mutate } = useSWR<AccessibilityPreference>(isSignedIn ? preferencesUrl : null, fetcher, {
    revalidateOnFocus: false,
  })

  useEffect(() => {
    dataRef.current = data
    if (data) {
      setPreference(data)
    }
  }, [data])

  const persistPreference = useCallback(
    async (nextPreference: AccessibilityPreference) => {
      setSaving(true)
      setSaved(false)

      try {
        const savedPreference = await updater<AccessibilityPreference>(preferencesUrl, nextPreference)
        dataRef.current = savedPreference
        setPreference(savedPreference)
        await mutate(savedPreference, false)
        setSaved(true)
        if (savedTimeoutRef.current) {
          clearTimeout(savedTimeoutRef.current)
        }
        savedTimeoutRef.current = setTimeout(() => setSaved(false), 3000)
      } catch {
        setPreference(dataRef.current ?? DEFAULT_PREFERENCE)
        toast.error(t('accessibilityPreferences.errorMessage'))
      } finally {
        setSaving(false)
      }
    },
    [mutate, preferencesUrl, t, updater]
  )

  const debouncedPersistPreference = useMemo(
    () =>
      debounce((nextPreference: AccessibilityPreference) => void persistPreference(nextPreference), SAVE_DEBOUNCE_MS),
    [persistPreference]
  )

  useEffect(() => {
    return () => {
      debouncedPersistPreference.cancel()
      if (savedTimeoutRef.current) {
        clearTimeout(savedTimeoutRef.current)
      }
    }
  }, [debouncedPersistPreference])

  const savePreference = (nextPreference: AccessibilityPreference) => {
    setPreference(nextPreference)
    setSaved(false)
    debouncedPersistPreference(nextPreference)
  }

  const updatePreference = (patch: Partial<AccessibilityPreference>) => {
    const nextPreference: AccessibilityPreference = { ...preference, ...patch }

    savePreference(nextPreference)
  }

  const handleMayBenefitChange = (checked: boolean) =>
    updatePreference({
      may_benefit: checked,
      support_areas: checked ? preference.support_areas : [],
    })

  const handleHighContrastChange = (checked: boolean) => {
    updatePreference({ high_contrast_enabled: checked })
  }

  const handleSupportAreaChange = (area: AccessibilitySupportArea, checked: boolean) => {
    const supportAreas = checked
      ? [...preference.support_areas, area]
      : preference.support_areas.filter((selectedArea) => selectedArea !== area)

    savePreference({
      ...preference,
      may_benefit: preference.may_benefit,
      high_contrast_enabled: preference.high_contrast_enabled,
      support_areas: supportAreas,
    })
  }

  return (
    <div className='max-w-2xl space-y-6'>
      <div>
        <h2 className='mb-2 text-2xl font-bold text-gray-900'>{t('accessibilityPreferences.title')}</h2>
      </div>

      {isLoading ? (
        <div className='flex items-center gap-2 text-sm text-muted-foreground'>
          <Loader2 className='h-4 w-4 animate-spin' />
          {t('accessibilityPreferences.loading')}
        </div>
      ) : (
        <div className='space-y-5'>
          <div className='space-y-5 rounded-lg border border-gray-200 bg-white p-4'>
            <Label htmlFor='accessibility-may-benefit' className='flex cursor-pointer items-start gap-3'>
              <Checkbox
                id='accessibility-may-benefit'
                checked={preference.may_benefit}
                disabled={saving}
                onCheckedChange={(checked) => handleMayBenefitChange(checked === true)}
                className='mt-1'
              />
              <span className='space-y-2'>
                <span className='block text-sm font-medium text-gray-900'>
                  {t('accessibilityPreferences.question')}
                </span>
                <span className='block text-sm leading-6 text-gray-600'>
                  {t('accessibilityPreferences.description')}
                </span>
              </span>
            </Label>

            <div className='space-y-3'>
              <p className='text-sm font-medium text-gray-900'>{t('accessibilityPreferences.areasPrompt')}</p>
              <div className='space-y-2'>
                {SUPPORT_AREAS.map((area) => {
                  const checked = preference.support_areas.includes(area)
                  const disabled = saving || !preference.may_benefit

                  return (
                    <Label
                      key={area}
                      htmlFor={`accessibility-area-${area}`}
                      className={`flex items-center gap-3 rounded-md border border-gray-200 p-3 text-sm ${
                        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-gray-50'
                      }`}
                    >
                      <Checkbox
                        id={`accessibility-area-${area}`}
                        checked={checked}
                        disabled={disabled}
                        onCheckedChange={(nextChecked) => handleSupportAreaChange(area, nextChecked === true)}
                      />
                      <span>{t(`accessibilityPreferences.areas.${area}`)}</span>
                    </Label>
                  )
                })}
              </div>
            </div>
          </div>

          <div className='rounded-lg border border-gray-200 bg-white p-4'>
            <div className='flex items-center justify-between gap-4'>
              <Label htmlFor='accessibility-high-contrast' className='cursor-pointer text-sm font-medium text-gray-900'>
                {t('accessibilityPreferences.highContrastModeLabel')}
              </Label>
              <Switch
                id='accessibility-high-contrast'
                checked={preference.high_contrast_enabled}
                disabled={saving}
                onCheckedChange={handleHighContrastChange}
              />
            </div>
          </div>
        </div>
      )}

      {(saving || saved) && (
        <p role='status' aria-live='polite' className='flex items-center gap-2 text-sm text-muted-foreground'>
          {saving ? <Loader2 className='h-4 w-4 animate-spin' /> : <Check className='h-4 w-4 text-green-600' />}
          {saving ? t('accessibilityPreferences.saving') : t('accessibilityPreferences.saved')}
        </p>
      )}
    </div>
  )
}
